import { VoiceStrategy } from './VoiceStrategy';
import { ComposerSession } from '../ComposerSession';
import { HarmonyEngine } from '../HarmonyEngine';
import { RhythmEngine } from '../RhythmEngine';
import { MelodyGenerator } from '../MelodyGenerator';
import { StructureEngine } from '../StructureEngine';
import { midiToFreq } from '../../../utils/math';
import { HarmonyUtils } from '../../../utils/music/HarmonyUtils';

export class MaestroVoice implements VoiceStrategy {
    private lastNoteTime: number = 0;

    constructor(
        private harmonyEngine: HarmonyEngine,
        private rhythmEngine: RhythmEngine,
        private melodyGenerator: MelodyGenerator,
        private structureEngine: StructureEngine
    ) { }

    getNextEvent(
        session: ComposerSession,
        scaleNotes: number[],
        baseVelocity: number,
        sectionVelMult: number,
        timbreData: { filter: number; release: number; reverb: number },
        tuning: number,
        dynamics?: { velocity: number; yPos: number },
        density?: 'sparse' | 'normal' | 'dense'
    ): {
        events: Array<{
            freq: number;
            delay: number;
            duration?: number;
            velocity?: number;
            timbre?: { filter: number; release: number; reverb: number };
            dampAll?: boolean;
        }>;
        stateUpdate?: {
            currentChordContext?: number[];
            currentBassNote?: number;
            lastMelodyNote?: number;
        };
    } {
        // 1. Forecast Management
        const conclusionState = this.structureEngine.getConclusionState();
        const touchesRemaining = this.structureEngine.getTouchesRemaining();

        // V12.5: Ensure forecast, but stop filling if near end
        this.harmonyEngine.ensureForecast(session, scaleNotes, conclusionState, touchesRemaining);

        // P0 FIX: EXTENDED LOOKAHEAD RESOLUTION
        // Aggressively rewrite forecast at 12 touches remaining (not 5)
        const resolutionLookahead = this.harmonyEngine.getResolutionLookahead();
        if (touchesRemaining <= resolutionLookahead) {
            this.harmonyEngine.updateResolutionForecast(session, touchesRemaining, scaleNotes);
        }

        // 2. Advance Logic
        // Force advance if user is playing in the "Bass Zone" (Bottom 30%)
        const isForcedProgression = dynamics ? dynamics.yPos > 0.7 : false;

        let didAdvance = false;
        // P0 FIX: Force progression advance throughout the finale sequence (last 12 touches)
        // This ensures the extended resolution sequence plays out instead of being throttled by rhythm.
        const shouldForceFinale = touchesRemaining <= resolutionLookahead;

        if (shouldForceFinale || this.rhythmEngine.shouldAdvanceProgression(isForcedProgression, this.structureEngine.getHarmonicRhythmDuration())) {
            this.harmonyEngine.advanceChord(session);
            this.advanceSession(session); // Keep index in sync for structure engine reference
            didAdvance = true;

            // AUDIT FIX 1.3: Notify HarmonicStorytelling of section context
            this.harmonyEngine.onSectionChange(this.structureEngine.getCurrentSection());
        }

        // 3. Get Current Harmonic Context from Memory
        // Instead of calculating from scratch, we look at the 'Now' chord from history (or fallback)
        const currentChord = this.harmonyEngine.getCurrentChord(session);

        // Map Forecast Data to Local Variables
        // Default values if no history yet (shouldn't happen due to ensureForecast bootstrap)
        let effectiveQuality = currentChord?.quality || 'major';
        let effectiveRoot = currentChord?.rootMidi || scaleNotes[0];
        let rawChordNotes = currentChord?.notes || [];
        // AUDIT FIX 5.1: GRAND FINALE SAFETY
        // If we are on the very last touch, FORCE the Tonic (I) chord.
        // No matter what the forecast says (even if Jazz logic put a bII7 here),
        // we must resolve home at the very end.
        // P4 FIX: Enhanced finale handling with maximum impact
        const isFinale = touchesRemaining <= 1;
        const isPenultimate = touchesRemaining === 2;  // P4: Dramatic pause position

        if (isFinale) {
            effectiveRoot = scaleNotes[0]; // Tonic
            effectiveQuality = 'major';
            rawChordNotes = this.harmonyEngine.getChromaticChord(effectiveRoot, effectiveQuality);
        }

        // AUDIT FIX 1.2: Override with Journey Hint if provided
        if (session.journeyOverride && session.journeyOverride.length > 0) {
            rawChordNotes = session.journeyOverride;
            effectiveRoot = session.journeyOverride[0];
            // effectiveQuality stays 'major' (safe default) or could be analyzed
        }

        // We still need rootScaleIndex for MelodyGenerator
        const rawDegree = currentChord?.degree || 1;
        const rootScaleIndex = HarmonyUtils.findScaleIndexForDegree(scaleNotes, rawDegree, session.octaveOffset);

        // AUDIT FIX 1.3: Apply HarmonicStorytelling decisions
        const intensity = dynamics?.velocity ?? 0.7;
        const nextChord = session.chordForecast.length > 0 ? session.chordForecast[0] : null;
        const targetDegree = nextChord?.degree || 1;

        const storytellingDecision = this.harmonyEngine.getStorytellingDecision(
            rawDegree,
            effectiveQuality,
            targetDegree,
            this.structureEngine.getCurrentSection(),
            intensity,
            session.progression.complexity // V12.5: Genre Awareness
        );

        // Apply deceptive cadence if triggered (only if not overridden)
        if (!session.journeyOverride) {
            if (storytellingDecision.useDeceptiveCadence && storytellingDecision.deceptiveTarget !== null) {
                // Override the chord to vi instead of expected I
                const deceptiveRoot = this.harmonyEngine.applyDeceptiveCadence(rawDegree, storytellingDecision.deceptiveTarget, scaleNotes);
                effectiveRoot = deceptiveRoot;
                effectiveQuality = 'minor'; // vi is minor in major key
                rawChordNotes = this.harmonyEngine.getChromaticChord(effectiveRoot, effectiveQuality);
            }

            // V12.5: Apply Genre-Aware Modulation
            if (storytellingDecision.modulation.shouldModulate) {
                session.currentKeyOffset = this.harmonyEngine.applyKeyLift(
                    session.currentKeyOffset,
                    storytellingDecision.modulation.semitones
                );
            }

            // Apply tritone substitution if triggered
            if (storytellingDecision.tritoneSubstitution.shouldUse && rawDegree === 5) {
                const tritoneData = this.harmonyEngine.getTritoneSubstitution(effectiveRoot);
                effectiveRoot = tritoneData.rootMidi;
                rawChordNotes = tritoneData.notes;
                effectiveQuality = 'dominant7';
            }
        }

        // Harmonic Rhythm Check
        // AUDIT FIX 1.8: Ideally should consolidate, but for now we keep the call.
        this.rhythmEngine.incrementTouch();
        const rhythmDecision = this.rhythmEngine.shouldTriggerChord(session.textureMode, dynamics);
        const shouldPlayChord = rhythmDecision.shouldPlay || !!session.journeyOverride; // Force play if overridden

        let chordNotes: number[] = [];
        let resolutionNotes: { note: number, delayRatio: number }[] = [];
        let bassNote = effectiveRoot; // Default

        if (shouldPlayChord) {
            // Generate notes if we haven't already
            if (rawChordNotes.length === 0) {
                rawChordNotes = this.harmonyEngine.getChordNotes(scaleNotes, rootScaleIndex, effectiveQuality, session.scale, effectiveRoot);
            }

            // Voice Leading
            const phraseFunction = this.structureEngine.getPhraseFunction();
            const voiceLedNotes = this.harmonyEngine.getVoiceLedChord(rawChordNotes, session.currentMood, phraseFunction);

            // Harmonic Glue (Suspensions)
            const suspendedData = this.harmonyEngine.getSuspendedChord(voiceLedNotes, scaleNotes, effectiveQuality, effectiveRoot, phraseFunction);
            chordNotes = suspendedData.mainNotes;
            resolutionNotes = suspendedData.resolutionNotes;

            // Smart Bass
            bassNote = this.harmonyEngine.getSmartBassNote(scaleNotes, effectiveRoot, effectiveQuality, session.currentBassNote);
        }

        // Side Effects (State Updates)
        const stateUpdate: { currentChordContext?: number[], currentBassNote?: number, lastMelodyNote?: number } = {};
        if (shouldPlayChord) {
            stateUpdate.currentBassNote = bassNote;
        }

        // MAESTRO CONTEXT UPDATE (Always update context if we have new chord notes, otherwise it stays stale/sustains)
        if (chordNotes.length > 0) {
            stateUpdate.currentChordContext = chordNotes;
        }

        // MELODY GENERATION (Harmony + Melody)
        // AUDIT FIX 2.6: Use actual metric position for strong beats
        const grooveCtx = this.rhythmEngine.getGrooveContext();
        let isStrongBeat = false;
        if (grooveCtx.confidence > 0.3) {
            // Strong beat = phase near 0.0 or 0.5 (on the beat)
            const phase = grooveCtx.beatPhase;
            isStrongBeat = phase < 0.2 || phase > 0.8 || (phase > 0.45 && phase < 0.55);
        } else {
            // Low confidence (random playing) -> Default to Strong Beat (Safe Chords) to anchor the chaos
            // AUDIT FIX 4.1: Increased safety from 0.4 to 0.8. 
            // When the user plays erratically, we should provide MORE structure, not less.
            isStrongBeat = Math.random() < 0.8;
        }

        // Use current context if we didn't generate a new chord this frame
        const guideChordNotes = chordNotes.length > 0 ? chordNotes : session.currentChordContext;

        // V10: Get register settings for octave shifting
        const registerSettings = this.structureEngine.getRegisterSettings();

        // V10: Get anticipation data from forecast
        const progressionProgress = this.rhythmEngine.getProgressionProgress();
        const anticipationChord = session.chordForecast.length > 0 ? session.chordForecast[0] : null;
        const nextChordNotes = anticipationChord ? anticipationChord.notes : [];

        // P2 FIX: Get cadence type for melody targeting
        // P2 FIX: Get cadence type for melody targeting
        const cadenceType = this.structureEngine.getCadenceType();

        // V13: HOOK ARCHITECT LOGIC
        // Determine whether to Record, Replay, or ignore the Hook based on section state
        let hookMode: 'record' | 'replay' | 'none' = 'none';
        const currentSectionName = this.structureEngine.getCurrentSection();

        if (currentSectionName === 'Chorus') {
            if (session.hasRecordedHook) {
                hookMode = 'replay';
            } else {
                hookMode = 'record';
                // Safe-guard: If we are recording, ensure we clear any garbage if this is the start?
                // Actually reset happens at Session init.
                // But if we have multiple "First Choruses" due to reset? Session init handles it.
            }
        }

        // Phase 2: Rhythmic Capture
        const now = Date.now();
        const timeSinceLastNote = this.lastNoteTime > 0 ? (now - this.lastNoteTime) / 1000 : 0.5;
        this.lastNoteTime = now;
        const detectedBPM = grooveCtx.confidence > 0.4 ? grooveCtx.detectedBPM : 120; // Lower confidence threshold for recording?

        const melodyResult = this.melodyGenerator.getNextMelodyNote(
            scaleNotes,
            rootScaleIndex,
            currentSectionName,
            session.currentMood,
            bassNote,
            guideChordNotes,
            isStrongBeat,
            null,  // primaryVoiceNote (not used in MaestroVoice)
            nextChordNotes,         // V10: Anticipation
            progressionProgress,    // V10: Anticipation progress
            cadenceType,             // P2 FIX: Cadence type for melody targeting
            hookMode,               // V13: Hook Mode
            session.hookMotif,      // V13: Hook Buffer
            timeSinceLastNote,      // Phase 2: Rhythm Capture
            detectedBPM             // Phase 2: Rhythm Quantization
        );

        const melodyNote = melodyResult.note;
        const rhythmicOverride = melodyResult.rhythmicOverride;

        // Structure Advance
        // AUDIT FIX 1.7: Monitor section changes
        const prevSection = this.structureEngine.getCurrentSection();
        // PHASE 2: Pass intensity for adaptive pacing
        const modEvent = this.structureEngine.advanceTouch(dynamics?.velocity);
        const currentSection = this.structureEngine.getCurrentSection();

        if (prevSection !== currentSection) {
            this.harmonyEngine.onSectionChange(currentSection);

            // V13: HOOK COMPLETION CHECK
            // If we just finished a Chorus and were recording, save the state.
            if (prevSection === 'Chorus' && !session.hasRecordedHook && session.hookMotif.length > 0) {
                session.hasRecordedHook = true;
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[MaestroVoice] Hook Recorded! Length: ${session.hookMotif.length}`);
                }
            }
        }

        // --- LATE STATE UPDATE: MELODY NOTE ---
        // Must be done here because melodyNote is generated above
        if (melodyNote !== null) {
            stateUpdate.lastMelodyNote = melodyNote;
        }
        if (modEvent) {
            this.handleModulation(session, modEvent);
        }

        // AUDIT FIX 1.5: Use unified density from ComposerOrchestrator (ArrangementIntelligence)
        // Fallback to structure density if not provided
        const effectiveDensity = density || this.structureEngine.getPhraseDensity();

        // V10: Apply register shifting to bass and melody
        const rawShiftedBass = bassNote + (registerSettings.bassOctave * 12);
        // AUDIT FIX 2.5: Clamp shifted bass to max MIDI 58 (Bb3)
        const shiftedBassNote = Math.min(58, rawShiftedBass);

        const shiftedMelodyNote = melodyNote + (registerSettings.melodyOctave * 12);

        // P0 FIX: Get ritardando factor for natural song endings
        const ritardandoFactor = this.structureEngine.getRitardandoFactor();

        // PATTERN GENERATION
        // P4 FIX: Dramatic pause - return silence on penultimate touch
        if (isPenultimate) {
            // Create a dramatic pause before the final chord
            // Return minimal output with a "breath" indicator
            if (process.env.NODE_ENV === 'development') {
                console.log(`[MaestroVoice] DRAMATIC PAUSE before finale`);
            }

            // Return a single quiet sustain of the previous chord (held breath)
            // or silence
            // Return a single quiet sustain of the previous chord (held breath)
            // or silence
            return {
                events: [{
                    freq: midiToFreq(bassNote + session.currentKeyOffset, tuning),
                    delay: 0,
                    velocity: 0.15,  // Very quiet sustain (breath)
                    duration: 0.8,   // Short, anticipatory
                    timbre: timbreData
                }],
                stateUpdate
            };
        }

        const currTempoMult = this.rhythmEngine.getAdaptiveTempoMultiplier(ritardandoFactor);

        const pattern = this.rhythmEngine.generatePattern(
            chordNotes,  // Chord notes keep their voicing
            shiftedBassNote,   // V10: Register-shifted bass
            shiftedMelodyNote, // V10: Register-shifted melody
            {
                section: this.structureEngine.getCurrentSection(),
                tempoMult: currTempoMult, // P0 FIX: Apply ritardando
                baseVelocity,
                sectionDynamics: sectionVelMult,
                tuning,
                currentKeyOffset: session.currentKeyOffset,
                timbre: timbreData,
                density: effectiveDensity,  // V10: Rhythmic breathing (Unified)
                cadenceType,                // P3 FIX: Cadence-aware patterns
                phraseFunction: this.structureEngine.getPhraseFunction()  // P3 FIX: Articulation variation
            }
        );

        // FEATURE 3: SHADOW HARMONIZER ("Auto-Divisi")
        const section = this.structureEngine.getCurrentSection();
        const isHighIntensity = section === 'Chorus' || section === 'Bridge';

        if (isHighIntensity && melodyNote && !shouldPlayChord) {
            const shadows = HarmonyUtils.getShadowNotes(
                melodyNote,
                guideChordNotes.length > 0 ? guideChordNotes : rawChordNotes,
                section === 'Bridge' ? 2 : 1
            );

            shadows.forEach(note => {
                pattern.push({
                    freq: midiToFreq(note + session.currentKeyOffset, tuning),
                    delay: 0.05, // Slightly loose
                    velocity: baseVelocity * sectionVelMult * 0.6, // Ghost velocity
                    timbre: timbreData
                });
            });
        }

        // Append Resolutions
        if (resolutionNotes.length > 0) {
            resolutionNotes.forEach(res => {
                const delay = 0.4 + (Math.random() * 0.2);
                pattern.push({
                    freq: midiToFreq(res.note + session.currentKeyOffset, tuning),
                    delay: delay,
                    velocity: baseVelocity * sectionVelMult * 0.8,
                    timbre: timbreData
                });
            });
        }

        const events = pattern.map((p, i) => {
            if (i === 0 && didAdvance) {
                // P4 FIX: ENHANCED GRAND FINALE
                // Maximum velocity, longest sustain, damp everything else
                if (isFinale) {
                    if (process.env.NODE_ENV === 'development') {
                        console.log(`[MaestroVoice] GRAND FINALE - Maximum impact!`);
                    }
                    return {
                        ...p,
                        dampAll: true,
                        duration: 8.0,    // P4: Extended sustain (8 seconds)
                        velocity: 1.0,    // P4: Maximum velocity
                        isFinale: true    // Flag for audio service to apply special treatment
                    };
                }
                return { ...p, dampAll: true };
            }

            // Phase 2: Rhythmic Motif Override (Articulation)
            const expectedFreq = midiToFreq(shiftedMelodyNote + session.currentKeyOffset, tuning);
            // Allow small epsilon for float comparison
            if (Math.abs(p.freq - expectedFreq) < 0.1 && rhythmicOverride !== undefined) {
                const effectiveBPM = 120 * currTempoMult;
                const durationSec = rhythmicOverride * (60 / effectiveBPM);
                return { ...p, duration: durationSec };
            }

            return p;
        });

        return {
            events,
            stateUpdate
        };
    }

    private advanceSession(session: ComposerSession) {
        session.progressionIndex = (session.progressionIndex + 1) % session.progression.degrees.length;
    }

    private handleModulation(session: ComposerSession, event: 'ToRelativeMinor' | 'LiftWholeStep' | 'Reset') {
        if (event === 'Reset') {
            session.currentKeyOffset = 0;
            // console.log(`[Composer] Modulation: Reset`);
        } else if (event === 'ToRelativeMinor') {
            if (session.scale === 'major' || session.scale === 'pentatonic_major') {
                session.currentKeyOffset = 7;
            } else {
                session.currentKeyOffset = 3;
            }
            // console.log(`[Composer] Modulation: Bridge Tension`);
        } else if (event === 'LiftWholeStep') {
            session.currentKeyOffset = 2;
            // console.log(`[Composer] Modulation: Lift`);
        }
    }
}
